import React, { useState, useEffect } from 'react';
import { IndianRupee, Calendar, AlertCircle, CheckCircle, Clock, History } from 'lucide-react';
import { toast } from 'react-toastify';
import { listInvoices } from '../utils/db/invoices'; // We might need a modified version or just filter client-side
import PaymentModal from './PaymentModal';
import { useAuth } from '../utils/AuthContext';
import { supabase } from '../utils/supabaseClient';

const PaymentList = ({ type }) => {
    const { user } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [statusFilter, setStatusFilter] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (user?.id) {
            loadInvoices();
        }
    }, [user?.id, type]);

    const loadInvoices = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('invoices')
                .select('*, customers(customer_name)')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setInvoices(data || []);
        } catch (error) {
            console.error('Error loading invoices:', error);
            toast.error('Failed to load invoices');
        } finally {
            setLoading(false);
        }
    };

    const getFilteredInvoices = () => {
        return invoices.filter(inv => {
            const isNG = inv.invoice_no?.toString().startsWith('NG-');

            // 1. Filter by Type (GST/Non-GST)
            if (type === 'gst' && isNG) return false;
            if (type === 'non-gst' && !isNG) return false;

            // 2. Filter by Status
            const status = inv.payment_status || 'PENDING';
            if (statusFilter === 'Paid' && status !== 'PAID') return false;
            if (statusFilter === 'Unpaid' && status === 'PAID') return false;

            // 3. Filter by Customer Search
            if (searchTerm) {
                const customerName = inv.customers?.customer_name?.toLowerCase() || '';
                const invoiceNo = inv.invoice_no?.toString().toLowerCase() || '';
                if (!customerName.includes(searchTerm.toLowerCase()) && !invoiceNo.includes(searchTerm.toLowerCase())) {
                    return false;
                }
            }

            return true;
        }).sort((a, b) => {
            const statusA = a.payment_status || 'PENDING';
            const statusB = b.payment_status || 'PENDING';
            if (statusA === statusB) return 0;
            if (statusA === 'PAID') return 1;
            if (statusB === 'PAID') return -1;
            return 0;
        });
    };

    const handleRecordPayment = (invoice) => {
        setSelectedInvoice(invoice);
        setIsModalOpen(true);
    };

    const handleSavePayment = async (paymentData) => {
        try {
            // 1. Insert Payment
            const { error: payError } = await supabase.from('payments').insert({
                user_id: user.id,
                invoice_id: paymentData.invoiceId,
                amount: paymentData.amount,
                tds_amount: paymentData.tdsAmount || 0,
                payment_date: paymentData.paymentDate,
                payment_mode: paymentData.paymentMode,
                reference_number: paymentData.referenceNumber,
                notes: paymentData.notes
            });
            if (payError) throw payError;

            // 2. Update Invoice Status
            // We need to fetch current amount_paid to add to it, or recalculate from sum of payments
            // Best practice: Recalculate sum of all payments for this invoice
            const { data: payments, error: sumError } = await supabase
                .from('payments')
                .select('amount, tds_amount')
                .eq('invoice_id', paymentData.invoiceId);

            if (sumError) throw sumError;

            const totalPaid = payments.reduce((sum, p) => sum + parseFloat(p.amount) + parseFloat(p.tds_amount || 0), 0);
            const totalTDS = payments.reduce((sum, p) => sum + parseFloat(p.tds_amount || 0), 0);
            const grandTotal = parseFloat(selectedInvoice.grand_total || 0);

            let newStatus = 'PENDING';
            if (totalPaid >= grandTotal - 1 && totalPaid > 0) { // 1 rupee tolerance
                newStatus = 'PAID';
            } else if (totalPaid > 0) {
                newStatus = 'PARTIAL';
            }

            const { error: updateError } = await supabase
                .from('invoices')
                .update({
                    amount_paid: totalPaid,
                    tds_amount: totalTDS,
                    payment_status: newStatus
                })
                .eq('id', paymentData.invoiceId);

            if (updateError) throw updateError;

            toast.success('Payment recorded successfully');
            setIsModalOpen(false);
            loadInvoices(); // Refresh list

        } catch (error) {
            console.error(error);
            toast.error('Failed to save payment');
        }
    };

    const getStatusBadge = (status) => {
        const s = status || 'PENDING';
        switch (s) {
            case 'PAID':
                return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" /> Paid</span>;
            case 'PARTIAL':
                return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" /> Partial</span>;
            default:
                return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><AlertCircle className="w-3 h-3 mr-1" /> Pending</span>;
        }
    };

    // Derived statistics should be based on the CURRENT TYPE (GST/Non-GST) regardless of status filter?
    // Usually summary boxes show global stats for that tab. 
    // Filter should likely affect the TABLE list.
    // Let's first filter by TYPE getting `typeInvoices` then apply status filter for `tableInvoices`.

    const typeInvoices = invoices.filter(inv => {
        const isNG = inv.invoice_no?.toString().startsWith('NG-');
        if (type === 'gst' && isNG) return false;
        if (type === 'non-gst' && !isNG) return false;
        return true;
    });

    const totalStats = typeInvoices.reduce((acc, curr) => {
        const total = parseFloat(curr.grand_total || 0);
        const paid = parseFloat(curr.amount_paid || 0);
        return {
            sales: acc.sales + total,
            paid: acc.paid + paid,
            unpaid: acc.unpaid + (total - paid)
        };
    }, { sales: 0, paid: 0, unpaid: 0 });

    const filteredInvoices = getFilteredInvoices();

    if (loading && invoices.length === 0) return <div className="p-4 text-center">Loading...</div>;

    return (
        <>
            {/* Payment Summary Boxes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-green-50 rounded-lg p-4 shadow-sm border border-green-100">
                    <div className="flex items-center">
                        <div className="p-3 rounded-full bg-green-100 text-green-600">
                            <CheckCircle className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-green-900">Total Paid Amount</p>
                            <p className="text-2xl font-bold text-green-700">
                                ₹{totalStats.paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-red-50 rounded-lg p-4 shadow-sm border border-red-100">
                    <div className="flex items-center">
                        <div className="p-3 rounded-full bg-red-100 text-red-600">
                            <AlertCircle className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-red-900">Total Unpaid Amount</p>
                            <p className="text-2xl font-bold text-red-700">
                                ₹{totalStats.unpaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 shadow-sm border border-blue-100">
                    <div className="flex items-center">
                        <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                            <IndianRupee className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-blue-900">Total Sales</p>
                            <p className="text-2xl font-bold text-blue-700">
                                ₹{totalStats.sales.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter and Search Bar */}
            <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                    <input
                        type="text"
                        placeholder="Search by Customer Name or Invoice No..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full px-4 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md border"
                    />
                </div>
                <div className="w-full md:w-64">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md border"
                    >
                        <option value="All">All Status</option>
                        <option value="Paid">Paid</option>
                        <option value="Unpaid">Unpaid (Pending/Partial)</option>
                    </select>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice No</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TDS</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredInvoices.length > 0 ? filteredInvoices.map((invoice) => {
                            const total = parseFloat(invoice.grand_total || 0);
                            const paid = parseFloat(invoice.amount_paid || 0);
                            const balance = total - paid;

                            return (
                                <tr key={invoice.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {invoice.invoice_date}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {invoice.invoice_no}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {invoice.customers?.customer_name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        ₹{total.toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                                        ₹{(paid - parseFloat(invoice.tds_amount || 0)).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        ₹{parseFloat(invoice.tds_amount || 0).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600">
                                        ₹{balance > 0 ? balance.toLocaleString() : '0'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {getStatusBadge(invoice.payment_status)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        {(invoice.payment_status !== 'PAID') ? (
                                            <button
                                                onClick={() => handleRecordPayment(invoice)}
                                                className="text-primary-600 hover:text-primary-900 font-medium flex items-center justify-end w-full"
                                            >
                                                <IndianRupee className="w-4 h-4 mr-1" /> Pay
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleRecordPayment(invoice)}
                                                className="text-gray-500 hover:text-gray-700 font-medium flex items-center justify-end w-full"
                                            >
                                                <History className="w-4 h-4 mr-1" /> History
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        }) : (
                            <tr>
                                <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                                    No invoices found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <PaymentModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSavePayment}
                onRefresh={loadInvoices}
                invoice={selectedInvoice}
            />
        </>
    );
};

export default PaymentList;
